/**
 * Webtech HR Engine™ - Manager Skills Dashboard
 * Vue des compétences de l'équipe pour les managers
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Users,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Eye,
  BarChart3,
  Loader2,
  ChevronRight,
  UserCheck,
  Star,
  GraduationCap
} from 'lucide-react';

const IMPORTANCE_COLORS = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700'
};

export default function ManagerSkillsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [teamSkills, setTeamSkills] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user?.id && user?.companyId) {
      fetchTeamSkills();
    }
  }, [user?.id, user?.companyId]);

  const fetchTeamSkills = async () => {
    try {
      const response = await api.get(`/skills-intelligence/team/${user.id}?company_id=${user.companyId}`);
      setTeamSkills(response.data);
    } catch (error) {
      console.error('Error fetching team skills:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Satisfaisant';
    if (score >= 40) return 'À améliorer';
    return 'Critique';
  };

  if (loading) {
    return (
      <DashboardLayout title="Compétences équipe">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  const { team = [], summary = {} } = teamSkills || {};

  return (
    <DashboardLayout title="Compétences équipe">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Compétences de mon équipe</h1>
            <p className="text-slate-500">Suivez et développez les compétences de vos collaborateurs</p>
          </div>
          <Button
            onClick={() => navigate('/skills/campaigns')}
            className="bg-webtech-blue hover:bg-webtech-blue-hover"
          >
            <Target className="w-4 h-4 mr-2" />
            Évaluer les compétences
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Collaborateurs</p>
                  <p className="text-2xl font-bold">{summary.totalMembers || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Évalués</p>
                  <p className="text-2xl font-bold">{summary.evaluatedMembers || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Score moyen</p>
                  <p className={`text-2xl font-bold ${getScoreColor(summary.avgScore || 0)}`}>
                    {summary.avgScore || 0}%
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Écarts critiques</p>
                  <p className="text-2xl font-bold text-red-600">{summary.totalCriticalGaps || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team Members */}
        <Card>
          <CardHeader>
            <CardTitle>Mes collaborateurs</CardTitle>
            <CardDescription>Vue détaillée des compétences par collaborateur</CardDescription>
          </CardHeader>
          <CardContent>
            {team.length > 0 ? (
              <div className="space-y-4">
                {team.map((member) => (
                  <div
                    key={member.id}
                    className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-webtech-blue/20 flex items-center justify-center">
                          <span className="text-webtech-blue font-semibold">
                            {member.firstName?.[0]}{member.lastName?.[0]}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900">
                            {member.firstName} {member.lastName}
                          </h4>
                          <p className="text-sm text-slate-500">{member.position}</p>
                          {member.jobProfileName && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {member.jobProfileName}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {member.needsEvaluation ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            Non évalué
                          </Badge>
                        ) : (
                          <>
                            {/* Score */}
                            <div className="text-center">
                              <p className={`text-xl font-bold ${getScoreColor(member.overallScore || 0)}`}>
                                {member.overallScore || 0}%
                              </p>
                              <p className="text-xs text-slate-500">{getScoreLabel(member.overallScore || 0)}</p>
                            </div>

                            {/* Progress bar */}
                            <div className="w-32">
                              <Progress value={member.overallScore || 0} className="h-2" />
                            </div>

                            {/* Critical Gaps */}
                            {member.criticalGaps > 0 && (
                              <Badge className="bg-red-100 text-red-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {member.criticalGaps} critique(s)
                              </Badge>
                            )}
                          </>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/skills/employee/${member.id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Voir
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Aucun collaborateur dans votre équipe
                </h3>
                <p className="text-slate-500">
                  Vos collaborateurs apparaîtront ici une fois assignés.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/skills/campaigns')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900">Lancer une évaluation</h4>
                  <p className="text-sm text-slate-500">Évaluer les compétences de l'équipe</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/skills/upskilling')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900">Plans d'upskilling</h4>
                  <p className="text-sm text-slate-500">Proposer des plans de développement</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/skills/analytics')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900">Voir les rapports</h4>
                  <p className="text-sm text-slate-500">Analyser les tendances</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
